import React, { useState, useEffect, useCallback } from 'react';
import { getUserTasks, updateTask, deleteTask } from '../../api/user-admin';

interface ExtraTask {
    instanceId: string;
    type: string;
    amount: { points?: number; [key: string]: number | undefined };
    collected: boolean;
}

interface Task {
    instanceId: string;
    type: string;
    created: string;
    expires: string;
    layer: string;
    state: string;
    activationAmount: { points: number };
    completionAmount: { points: number };
    data: { target: number };
    extraTasks: ExtraTask[];
    streakDays: number;
    streakMultiplier: number;
}

const EXTRA_TASK_TYPES = [
    'watch_video',
    'solve_puzzle',
    'daily_challenge',
    'daily_article',
    'bonanza_1st_place',
    'bonanza_2nd_place',
    'bonanza_3rd_place',
    'bonanza_consolidation',
    'summer_boost',
    'autumn_boost',
    'spring_boost',
    'daily_game_1',
    'daily_game_2',
    'daily_game_3',
    'christmas_gift_large',
    'christmas_gift_medium',
    'christmas_gift_small',
];

const TaskTools: React.FC = () => {
    const [userId, setUserId] = useState('');
    const [allTasks, setAllTasks] = useState<Task[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        primary: true,
        weekend: true,
        seasonal: true,
        limitTo5: false
    });
    const [editingTask, setEditingTask] = useState<string | null>(null);
    const [editForm, setEditForm] = useState({
        created: '',
        expires: '',
        type: '',
        state: '',
        targetSteps: 0,
        streakDays: 0,
        streakMultiplier: 0,
        extraTasks: [] as ExtraTask[]
    });
    const [editingExtraTaskIndex, setEditingExtraTaskIndex] = useState<number | null>(null);
    const [newExtraTask, setNewExtraTask] = useState<Partial<ExtraTask>>({
        type: '',
        amount: { points: 0 },
        collected: false
    });

    const applyFilters = useCallback((tasksToFilter: Task[]) => {
        const dayMs = 24 * 60 * 60 * 1000;
        
        let filtered = tasksToFilter.filter((task: Task) => {
            // Filter by layer
            const layerMatch = 
                (filters.primary && task.layer === 'primary') ||
                (filters.weekend && task.layer === 'weekend') ||
                (filters.seasonal && task.layer === 'seasonal');
            
            if (!layerMatch) return false;
            
            // Filter by date range (keep existing date filtering logic)
            const isRecent = 
                ((task.layer === 'primary' || task.layer === 'weekend')
                && new Date(task.expires).getTime() > new Date().getTime() - (14 * dayMs))
                || (task.layer === 'seasonal' 
                    && new Date(task.expires).getTime() > new Date().getTime() - (180 * dayMs));
            
            return isRecent;
        });
        
        // Sort by created date (newest first)
        filtered.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
        
        // Limit to 5 if checkbox is checked
        if (filters.limitTo5) {
            filtered = filtered.slice(0, 5);
        }
        
        return filtered;
    }, [filters]);

    const fetchTasks = async () => {
        if (!userId.trim()) {
            setError('Please enter a user ID');
            return;
        }

        setLoading(true);
        setError('');
        
        const dayMs = 24 * 60 * 60 * 1000;

        try {
            const response = await getUserTasks(userId);
            const activeTasks = response.items.filter((task: Task) => 
                ((task.layer === 'primary' || task.layer === 'weekend')
                && new Date(task.expires).getTime() > new Date().getTime() - (14 * dayMs))
                || (task.layer === 'seasonal' 
                    && new Date(task.expires).getTime() > new Date().getTime() - (180 * dayMs))
            );
            setAllTasks(activeTasks);
            setTasks(applyFilters(activeTasks));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
        } finally {
            setLoading(false);
        }
    };

    // Update filtered tasks when filters change
    useEffect(() => {
        if (allTasks.length > 0) {
            setTasks(applyFilters(allTasks));
        }
    }, [filters, allTasks, applyFilters]);

    const handleEditTask = (task: Task) => {
        setEditingTask(task.instanceId);
        setEditForm({
            created: task.created,
            expires: task.expires,
            type: task.type,
            state: task.state,
            targetSteps: task.data?.target || 0,
            streakDays: task.streakDays || 0,
            streakMultiplier: task.streakMultiplier || 0,
            extraTasks: task.extraTasks ? [...task.extraTasks] : []
        });
        setEditingExtraTaskIndex(null);
        setNewExtraTask({ type: '', amount: { points: 0 }, collected: false });
    };

    const handleSaveTask = async (taskId: string) => {
        try {
            // Convert datetime-local values to ISO format
            const updates = {
                ...editForm,
                created: editForm.created ? new Date(editForm.created).toISOString() : editForm.created,
                expires: editForm.expires ? new Date(editForm.expires).toISOString() : editForm.expires,
            };
            await updateTask(userId, taskId, updates);
            setEditingTask(null);
            fetchTasks(); // Refresh the list
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update task');
        }
    };

    const handleDeleteTask = async (taskId: string) => {
        if (!window.confirm('Are you sure you want to delete this task?')) {
            return;
        }

        try {
            await deleteTask(userId, taskId);
            fetchTasks(); // Refresh the list
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete task');
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:mm)
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const generateExtraTaskId = () => {
        return `extra-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    const handleAddExtraTask = () => {
        if (!newExtraTask.type || !newExtraTask.amount?.points) {
            setError('Please fill in type and points for the extra task');
            return;
        }
        const extraTask: ExtraTask = {
            instanceId: generateExtraTaskId(),
            type: newExtraTask.type,
            amount: { points: newExtraTask.amount.points },
            collected: newExtraTask.collected || false
        };
        setEditForm({
            ...editForm,
            extraTasks: [...editForm.extraTasks, extraTask]
        });
        setNewExtraTask({ type: '', amount: { points: 0 }, collected: false });
        setError('');
    };

    const handleUpdateExtraTask = (index: number, field: keyof ExtraTask, value: any) => {
        const updated = [...editForm.extraTasks];
        updated[index] = { ...updated[index], [field]: value };
        setEditForm({ ...editForm, extraTasks: updated });
    };

    const handleDeleteExtraTask = (index: number) => {
        const updated = editForm.extraTasks.filter((_, i) => i !== index);
        setEditForm({ ...editForm, extraTasks: updated });
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <h1 className="text-2xl font-bold mb-6">Task Tools</h1>
                
                {/* User ID Input */}
                <div className="mb-6">
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-2">
                                User ID
                            </label>
                            <input
                                type="text"
                                id="userId"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter user ID"
                            />
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={fetchTasks}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? 'Loading...' : 'Fetch Tasks'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {/* Filter Checkboxes */}
                {allTasks.length > 0 && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-sm font-semibold mb-3">Filters</h3>
                        <div className="flex flex-wrap gap-4">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.primary}
                                    onChange={(e) => setFilters({...filters, primary: e.target.checked})}
                                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">Primary</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.weekend}
                                    onChange={(e) => setFilters({...filters, weekend: e.target.checked})}
                                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">Weekend</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.seasonal}
                                    onChange={(e) => setFilters({...filters, seasonal: e.target.checked})}
                                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">Seasonal</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.limitTo5}
                                    onChange={(e) => setFilters({...filters, limitTo5: e.target.checked})}
                                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">Limit to 5 latest</span>
                            </label>
                        </div>
                    </div>
                )}

                {/* Tasks List */}
                {tasks.length > 0 && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold">
                            Tasks {filters.limitTo5 ? '(5 latest)' : `(${tasks.length} total)`}
                        </h2>
                        {tasks.map((task) => (
                            <div key={task.instanceId} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-semibold text-lg">{task.type}</h3>
                                        <p className="text-sm text-gray-600">Layer: {task.layer}</p>
                                        <p className="text-sm text-gray-600">State: {task.state}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEditTask(task)}
                                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteTask(task.instanceId)}
                                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>

                                {/* Task Details */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                        <span className="font-medium">Created:</span>
                                        <p>{formatDate(task.created)}</p>
                                    </div>
                                    <div>
                                        <span className="font-medium">Expires:</span>
                                        <p>{formatDate(task.expires)}</p>
                                    </div>
                                    <div>
                                        <span className="font-medium">Target Steps:</span>
                                        <p>{task.data?.target || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="font-medium">Streak Days:</span>
                                        <p>{task.streakDays}</p>
                                    </div>
                                    <div>
                                        <span className="font-medium">Streak Multiplier:</span>
                                        <p>{task.streakMultiplier}</p>
                                    </div>
                                </div>

                                {/* Extra Tasks Display */}
                                {task.extraTasks && task.extraTasks.length > 0 && (
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-sm mb-2">Extra Tasks ({task.extraTasks.length})</h4>
                                        <div className="space-y-2">
                                            {task.extraTasks.map((extraTask, idx) => (
                                                <div key={extraTask.instanceId} className="bg-gray-50 p-2 rounded text-sm">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <span className="font-medium">{extraTask.type}</span>
                                                            <span className="ml-2 text-gray-600">
                                                                {extraTask.amount?.points || 0} points
                                                            </span>
                                                            <span className={`ml-2 px-2 py-1 rounded text-xs ${
                                                                extraTask.collected ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                            }`}>
                                                                {extraTask.collected ? 'Collected' : 'Not Collected'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Edit Form */}
                                {editingTask === task.instanceId && (
                                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="font-semibold mb-3">Edit Task</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Created Date
                                                </label>
                                                <input
                                                    type="datetime-local"
                                                    value={formatDateForInput(editForm.created)}
                                                    onChange={(e) => setEditForm({...editForm, created: e.target.value})}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Expires Date
                                                </label>
                                                <input
                                                    type="datetime-local"
                                                    value={formatDateForInput(editForm.expires)}
                                                    onChange={(e) => setEditForm({...editForm, expires: e.target.value})}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Type
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editForm.type}
                                                    onChange={(e) => setEditForm({...editForm, type: e.target.value})}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    State
                                                </label>
                                                <select
                                                    value={editForm.state}
                                                    onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                >
                                                    <option value="created">Created</option>
                                                    <option value="activated">Activated</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="collected">Collected</option>
                                                    <option value="expired_not_activated">Expired Not Activated</option>
                                                    <option value="expired_not_completed">Expired Not Completed</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Target Steps
                                                </label>
                                                <input
                                                    type="number"
                                                    value={editForm.targetSteps}
                                                    onChange={(e) => setEditForm({...editForm, targetSteps: parseInt(e.target.value) || 0})}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Streak Days
                                                </label>
                                                <input
                                                    type="number"
                                                    value={editForm.streakDays}
                                                    onChange={(e) => {
                                                        const days = parseInt(e.target.value) || 0;
                                                        const multiplier = days >= 7 ? 100 :
                                                                         days >= 6 ? 75 :
                                                                         days >= 5 ? 50 :
                                                                         days >= 4 ? 40 :
                                                                         days >= 3 ? 30 :
                                                                         days >= 2 ? 20 :
                                                                         days >= 1 ? 10 : 0;
                                                        setEditForm({...editForm, streakDays: days, streakMultiplier: multiplier});
                                                    }}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                />
                                            </div>
                                        </div>

                                        {/* Extra Tasks Section */}
                                        <div className="mt-6 border-t pt-4">
                                            <h4 className="font-semibold mb-3">Extra Tasks</h4>
                                            
                                            {/* Existing Extra Tasks */}
                                            {editForm.extraTasks.length > 0 && (
                                                <div className="space-y-3 mb-4">
                                                    {editForm.extraTasks.map((extraTask, index) => (
                                                        <div key={extraTask.instanceId} className="bg-white p-3 rounded border border-gray-200">
                                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                                                <div>
                                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                        Type
                                                                    </label>
                                                                    <select
                                                                        value={extraTask.type}
                                                                        onChange={(e) => handleUpdateExtraTask(index, 'type', e.target.value)}
                                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                                    >
                                                                        <option value="">Select type</option>
                                                                        {EXTRA_TASK_TYPES.map(type => (
                                                                            <option key={type} value={type}>{type}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                        Points
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        value={extraTask.amount?.points || 0}
                                                                        onChange={(e) => handleUpdateExtraTask(index, 'amount', { points: parseInt(e.target.value) || 0 })}
                                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                        Collected
                                                                    </label>
                                                                    <select
                                                                        value={extraTask.collected ? 'true' : 'false'}
                                                                        onChange={(e) => handleUpdateExtraTask(index, 'collected', e.target.value === 'true')}
                                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                                    >
                                                                        <option value="false">Not Collected</option>
                                                                        <option value="true">Collected</option>
                                                                    </select>
                                                                </div>
                                                                <div className="flex items-end">
                                                                    <button
                                                                        onClick={() => handleDeleteExtraTask(index)}
                                                                        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="mt-2 text-xs text-gray-500">
                                                                ID: {extraTask.instanceId}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Add New Extra Task */}
                                            <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                                <h5 className="font-medium text-sm mb-2">Add New Extra Task</h5>
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Type
                                                        </label>
                                                        <select
                                                            value={newExtraTask.type || ''}
                                                            onChange={(e) => setNewExtraTask({...newExtraTask, type: e.target.value})}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                        >
                                                            <option value="">Select type</option>
                                                            {EXTRA_TASK_TYPES.map(type => (
                                                                <option key={type} value={type}>{type}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Points
                                                        </label>
                                                        <input
                                                            type="number"
                                                            value={newExtraTask.amount?.points || 0}
                                                            onChange={(e) => setNewExtraTask({
                                                                ...newExtraTask, 
                                                                amount: { points: parseInt(e.target.value) || 0 }
                                                            })}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Collected
                                                        </label>
                                                        <select
                                                            value={newExtraTask.collected ? 'true' : 'false'}
                                                            onChange={(e) => setNewExtraTask({...newExtraTask, collected: e.target.value === 'true'})}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                        >
                                                            <option value="false">Not Collected</option>
                                                            <option value="true">Collected</option>
                                                        </select>
                                                    </div>
                                                    <div className="flex items-end">
                                                        <button
                                                            onClick={handleAddExtraTask}
                                                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                                                        >
                                                            Add
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex gap-2 mt-4">
                                            <button
                                                onClick={() => handleSaveTask(task.instanceId)}
                                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditingTask(null)}
                                                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* No Tasks Message */}
                {!loading && tasks.length === 0 && userId && (
                    <div className="text-center py-8 text-gray-500">
                        No active tasks found for this user.
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskTools;
